import _ from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilState } from 'recoil';
import { formatInteger } from '../common/formatHelper';
import {
  HeatmapBucket,
  HeatmapBucketStatField,
  getHeatmapBucketTitle,
} from '../common/heatmap.helper';
import { HeatmapMetricDescriptor } from '../common/heatmap.types';
import useFormatter from '../common/useFormatter';
import { cn } from '../common/utils';
import { Button } from '../components/actions/Button';
import * as Icon from '../components/icons';
import { Stat } from '../components/stats/Stat';
import { actualityDataSetTableTypeSelected } from '../feed/store/actuality.state';
import { useHeatmapSortOptions } from './HeatmapLegendPanelHeader';

export type HeatmapLegendBucketProps = {
  metricDescriptor: HeatmapMetricDescriptor<string, Record<string, any>>;
  bucket: HeatmapBucket;
  hasStats: boolean;
  dataField: HeatmapBucketStatField;
  isSelected: boolean;
  showPercentage: boolean;
  totalStats: Record<HeatmapBucketStatField, number>;
  onClick: React.MouseEventHandler<HTMLDivElement>;
  onBucketActionClick?: () => void;
  className?: string;
};

const HeatmapLegendBucket: React.FC<HeatmapLegendBucketProps> = props => {
  const { t } = useTranslation('app');
  const formatter = useFormatter();

  const {
    metricDescriptor,
    bucket,
    dataField,
    isSelected,
    showPercentage,
    totalStats,
  } = props;
  const sortOptions = useHeatmapSortOptions(props.hasStats);
  const [dataDisplayMode, setDataDisplayMode] = useRecoilState(
    actualityDataSetTableTypeSelected,
  );

  let title = getHeatmapBucketTitle(bucket, metricDescriptor, 'Unknown');

  const statValueRaw = bucket.stats?.[dataField];
  const total = totalStats[dataField] || 0;
  const percentageRaw = total > 0 ? (statValueRaw ?? 0) / total : 0;

  const statValue = _.isNil(statValueRaw)
    ? null
    : statValueRaw === 0
      ? t`Blank`
      : showPercentage
        ? `${formatter.formatShare(percentageRaw).fullString} (${formatInteger(statValueRaw)})`
        : formatInteger(statValueRaw);

  const hasDataAction =
    metricDescriptor?.rangeType === 'distinct' &&
    metricDescriptor?.type !== 'abc';

  const unitOfMeasureLabel = sortOptions[dataField]?.unitOfMeasure ?? null;

  return (
    <Stat
      data-component="heatmap-legend-bucket"
      className={props.className}
      titleElement={<div className={cn('truncate')}>{title}</div>}
      legendColor={[bucket.color, bucket.textColor]}
      namedColor={
        hasDataAction && {
          namedColorKey: title,
          palette: metricDescriptor.qualitativePaletteId,
        }
      }
      isActionable
      isSelectable
      inPanelMode
      isPreview
      toggleTable
      isSelected={isSelected}
      toggleIconOpen={() => null}
      toggleIconClosed={() => null}
      onClick={props.onClick}
      unitOfMeasure={unitOfMeasureLabel}
      switchUnitOfMeasure={
        // hasDataAction && (
        <Button
          className={cn(
            'border-menu-900 rounded border !p-1',
            'opacity-0 group-hover:opacity-100',
          )}
          buttonIcon={
            <Icon.ShowTable
              className={cn(
                'fill-current',
                // 'group-hover:opacity-50',
                'h-5 w-5 md:h-6 md:w-6',
              )}
            />
          }
          buttonSize="xs"
          isTransparent
          hasIconAfter
          onPress={() => {
            props.onBucketActionClick?.() ??
              setDataDisplayMode('activity-metric');
          }}
          // onPress={
          //   hasDataAction
          //     ? props.onBucketActionClick
          //     : () => {
          //         setDataDisplayMode('activity-metric');
          //       }
          // }
        />
        // )
      }
      value={statValue}
    />
  );
};

export default HeatmapLegendBucket;
